import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { events as eventsApi } from '../../lib/api';
import signalRService from '../../lib/signalr';
import HLSPlayer from '../../shared/components/HLSPlayer';
import ReactionBar from '../../shared/components/ReactionBar';
import { useAuth } from '../../shared/contexts/AuthContext';

function EventPage() {
  const { id } = useParams();
  const eventId = parseInt(id);
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStreamIdx, setActiveStreamIdx] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [performerAnnouncement, setPerformerAnnouncement] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => { loadEvent(); }, [id]);

  // Subscribe to all event streams for reactions/messages
  useEffect(() => {
    if (!event?.streams?.length || !user) return;

    const cleanups = [];
    const setup = async () => {
      for (const stream of event.streams) {
        const unsub1 = await signalRService.subscribeToStream(stream.streamId, () => {});
        cleanups.push(unsub1);
      }

      // Aggregate viewer count from all streams
      for (const stream of event.streams) {
        const unsub = await signalRService.onViewerCount(stream.streamId, () => {
          // Simple: just use count from any stream update
          setViewerCount(prev => prev + 1);
        });
        cleanups.push(unsub);
      }

      // Listen for messages on first stream (events share message channel via first stream)
      const primaryStreamId = event.streams[0].streamId;
      const unsub3 = await signalRService.onMessage(primaryStreamId, (data) => {
        if (data.isFromPerformer) {
          setPerformerAnnouncement(data);
          setTimeout(() => setPerformerAnnouncement(null), 10000);
        }
        setMessages(prev => [...prev.slice(-49), data]);
      });
      cleanups.push(unsub3);
    };

    setup();
    return () => cleanups.forEach(fn => fn?.());
  }, [event, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadEvent = async () => {
    try {
      const res = await eventsApi.getById(eventId);
      setEvent(res.data.event);
    } catch (err) {
      console.error('Failed to load event:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = (type) => {
    if (event?.streams?.[activeStreamIdx]) {
      signalRService.sendReaction(event.streams[activeStreamIdx].streamId, type);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !event?.streams?.[0]) return;
    await signalRService.sendMessage(event.streams[0].streamId, messageText.trim());
    setMessageText('');
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20 text-neutral-500">Loading...</div>;
  if (!event) return <div className="max-w-4xl mx-auto px-4 py-20 text-neutral-500">Event not found.</div>;

  const isLive = event.status === 'live';
  const activeStream = event.streams?.[activeStreamIdx];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link to="/" className="text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-white mb-6 inline-block">
        &larr; Back to events
      </Link>

      {/* Title */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold">{event.title}</h1>
          {isLive && (
            <span className="px-2 py-1 text-xs font-bold uppercase bg-red-600 text-white rounded animate-pulse">Live</span>
          )}
        </div>
        {event.description && <p className="text-neutral-600 dark:text-neutral-400">{event.description}</p>}
        <div className="flex items-center gap-4 mt-2 text-sm text-neutral-500">
          {event.location && <span>{event.location}</span>}
          {event.scheduledStart && !isLive && (
            <span>{new Date(event.scheduledStart).toLocaleString('da-DK', {
              weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
            })}</span>
          )}
          {viewerCount > 0 && <span>👁 {viewerCount} watching</span>}
        </div>
      </div>

      {/* Player */}
      {isLive && activeStream?.externalUrl ? (
        <>
          <HLSPlayer src={activeStream.externalUrl} autoPlay className="mb-4" />

          {/* Camera angle picker */}
          {event.streams.length > 1 && (
            <div className="flex gap-2 mb-4">
              {event.streams.map((stream, idx) => (
                <button
                  key={stream.streamId}
                  onClick={() => setActiveStreamIdx(idx)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    idx === activeStreamIdx
                      ? 'bg-blue-600 text-white'
                      : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                  }`}
                >
                  {stream.label || stream.name}
                </button>
              ))}
            </div>
          )}

          {/* Performer announcement */}
          {performerAnnouncement && (
            <div className="flex items-center gap-3 p-3 mb-4 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
              <span className="text-lg">📢</span>
              <span className="flex-1">{performerAnnouncement.text}</span>
              <button onClick={() => setPerformerAnnouncement(null)} className="text-neutral-400 hover:text-neutral-600">&times;</button>
            </div>
          )}

          {/* Reactions + Message */}
          {user ? (
            <div className="flex items-center gap-3 flex-wrap">
              <ReactionBar onReaction={handleReaction} />
              <form onSubmit={handleSendMessage} className="flex flex-1 min-w-[200px] gap-2">
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Send a message..."
                  maxLength={500}
                  className="flex-1 px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button type="submit" disabled={!messageText.trim()} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  Send
                </button>
              </form>
            </div>
          ) : (
            <div className="text-center py-4">
              <Link to="/login" className="text-blue-500 hover:underline text-sm">Sign in to react and send messages</Link>
            </div>
          )}

          {/* Messages */}
          {messages.length > 0 && (
            <div className="mt-6 max-h-60 overflow-y-auto space-y-2">
              {messages.map((msg, i) => (
                <div key={i} className={`text-sm px-3 py-2 rounded-lg ${msg.isFromPerformer ? 'bg-blue-50 dark:bg-blue-950 border-l-2 border-blue-500' : 'bg-neutral-100 dark:bg-neutral-900'}`}>
                  <span className="font-semibold text-xs text-neutral-500">{msg.isFromPerformer ? '📢 Performer' : msg.displayName}</span>
                  <p className="mt-0.5">{msg.text}</p>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </>
      ) : (
        /* Offline placeholder */
        <div className="relative rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
          <div className="aspect-video flex flex-col items-center justify-center bg-gradient-to-br from-neutral-200 to-neutral-300 dark:from-neutral-800 dark:to-neutral-900">
            <span className="text-6xl mb-4 opacity-50">🎵</span>
            <p className="text-2xl font-bold mb-1">
              {event.status === 'ended' ? 'Event has ended' : 'Event is upcoming'}
            </p>
            {event.scheduledStart && event.status === 'upcoming' && (
              <Countdown target={event.scheduledStart} />
            )}
            {event.streams?.length > 0 && (
              <p className="text-neutral-500 text-sm mt-4">
                {event.streams.length} camera{event.streams.length > 1 ? 's' : ''} configured
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Countdown({ target }) {
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) { setCountdown('Starting soon...'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${h > 0 ? `${h}h ` : ''}${m}m ${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [target]);

  return (
    <div className="text-center mt-4">
      <p className="text-neutral-500 text-sm uppercase tracking-wider">Starts in</p>
      <p className="text-3xl font-bold font-mono mt-1">{countdown}</p>
    </div>
  );
}

export default EventPage;
